import { GQL } from '@aline/graphql';

import type { UUID } from './type-helpers';
import { isUUIDV4 } from '../util/general';
import type { MentionType } from '../util/parseMarkdownMentions';

/**
 * Mention types whose URL is the plain `mention:<type>/<id>` form. The two
 * selection forms are special-cased below because they carry the encoded
 * selection context in a `?ctx=` query parameter.
 */
export type SimpleMentionType = Exclude<MentionType, 'selection' | 'selection-v2' | 'selection-v3'>;

type MentionUrlFor<TType extends SimpleMentionType> = `mention:${TType}/${string}`;

// Example: mention:document/doc-123
export type MentionUrlDocument = MentionUrlFor<'document'>;
// Example: mention:email/person@example.com
export type MentionUrlEmail = MentionUrlFor<'email'>;
// Example: mention:intake-form/current
export type MentionUrlIntakeForm = MentionUrlFor<'intake-form'>;
// Example: mention:selection/doc-123?ctx=c|0|c|Hello
export type MentionUrlSelection = `mention:selection/${string}?ctx=${string}`;
// Example: mention:selection-v2/doc-123?ctx=<base64-encoded-ExtractedContextV2>
export type MentionUrlSelectionV2 = `mention:selection-v2/${string}?ctx=${string}`;
// Example: mention:selection-v3/doc-123?ctx=<base64-encoded-SelectionContextV3>
export type MentionUrlSelectionV3 = `mention:selection-v3/${string}?ctx=${string}`;
// Example: mention:party/party-123
export type MentionUrlParty = MentionUrlFor<'party'>;
// Example: mention:task/task-123
export type MentionUrlTask = MentionUrlFor<'task'>;
// Example: mention:workflow/wf-123
export type MentionUrlWorkflow = MentionUrlFor<'workflow'>;
// Example: mention:report/report-123
export type MentionUrlReport = MentionUrlFor<'report'>;
// Example: mention:user/user-123
export type MentionUrlUser = MentionUrlFor<'user'>;
// Example: mention:playbook/playbook-123
export type MentionUrlPlaybook = MentionUrlFor<'playbook'>;
// Example: mention:playbook-rule/rule-123
export type MentionUrlPlaybookRule = MentionUrlFor<'playbook-rule'>;
// Example: mention:aline-ai
export type MentionUrlAlineAi = 'mention:aline-ai';

/**
 * Every valid mention URL, derived from MENTION_TYPES (see
 * `util/parseMarkdownMentions.ts`): the simple types expand through the
 * template literal, the two selection forms keep their `?ctx=` suffix, and
 * Aline AI is a reserved trigger mention with no id segment.
 */
export type MentionUrl =
  | MentionUrlAlineAi
  | MentionUrlFor<SimpleMentionType>
  | MentionUrlSelection
  | MentionUrlSelectionV2
  | MentionUrlSelectionV3;

export type MarkdownMention<TMentionUrl extends MentionUrl = MentionUrl> =
  `[@${string}](${TMentionUrl})`;

/**
 * Builds the canonical mention URL for a simple (non-selection) mention type.
 * Selection URLs carry an encoded `?ctx=` payload and are constructed by the
 * selection serializers instead.
 */
export function createMentionUrl<TType extends SimpleMentionType>(
  type: TType,
  id: string
): MentionUrlFor<TType> {
  return `mention:${type}/${id}`;
}

/** The intake form of the workflow the thread is bound to, resolved from the active context. */
export const CURRENT_INTAKE_FORM_MENTION_URL = createMentionUrl('intake-form', 'current');

/**
 * Path segment of a report-cell citation. It names one extracted value and
 * resolves to a report route on the client, so it is not a {@link MentionType}.
 */
export const REPORT_CELL_MENTION_PATH = 'report-cell';

export const REPORT_CELL_MENTION_PREFIX = `mention:${REPORT_CELL_MENTION_PATH}/`;

// Example: mention:report-cell/report-123/doc-123/question-123
export type MentionUrlReportCell =
  `mention:${typeof REPORT_CELL_MENTION_PATH}/${string}/${string}/${string}`;

/** The column of a report cell: an AI question, or a built-in document field such as Parties. */
export type ReportCellColumn =
  | { kind: 'question'; questionId: UUID }
  | { field: GQL.ReportColumnFileField; kind: 'field' };

export type ReportCell = { column: ReportCellColumn; documentId: UUID; reportId: UUID };

const REPORT_COLUMN_FILE_FIELDS: ReadonlySet<string> = new Set(
  Object.values(GQL.ReportColumnFileField)
);

/** The grid column field the cell sits in, which is also the last segment of its URL. */
export function getReportCellColumnKey(column: ReportCellColumn): string {
  switch (column.kind) {
    case 'question':
      return column.questionId;
    case 'field':
      return column.field;
  }
}

export function createReportCellMentionUrl({
  column,
  documentId,
  reportId,
}: ReportCell): MentionUrlReportCell {
  return `mention:${REPORT_CELL_MENTION_PATH}/${reportId}/${documentId}/${getReportCellColumnKey(column)}`;
}

/**
 * Reads a report-cell mention URL back into the cell it names. Null unless the
 * report and document are UUIDs and the column is a question UUID or a known
 * built-in field. A `?query` suffix is ignored.
 */
export function parseReportCellMentionUrl(href: string): ReportCell | null {
  if (!href.startsWith(REPORT_CELL_MENTION_PREFIX)) return null;

  const parts = href.slice(REPORT_CELL_MENTION_PREFIX.length).split('?')[0].split('/');
  if (parts.length !== 3) return null;
  const [reportId, documentId, columnKey] = parts;
  if (!isUUIDV4(reportId) || !isUUIDV4(documentId)) return null;

  const column = parseReportCellColumn(columnKey);
  return column ? { column, documentId, reportId } : null;
}

/** A grid column field as a cell column: a question UUID, or a built-in field. */
export function parseReportCellColumn(columnKey: string): ReportCellColumn | null {
  if (isUUIDV4(columnKey)) return { kind: 'question', questionId: columnKey };
  if (getIsReportColumnFileField(columnKey)) return { field: columnKey, kind: 'field' };
  return null;
}

function getIsReportColumnFileField(value: string): value is GQL.ReportColumnFileField {
  return REPORT_COLUMN_FILE_FIELDS.has(value);
}

/**
 * Report-column mentions are report-specific (used only in AI Reminder Automation prompts)
 * Example: mention:report-column/col-123
 */
export type AiColumnId = string;
export type ReportColumnId = GQL.ReportColumnFileField | AiColumnId;
export type MentionUrlReportColumn = `mention:report-column/${ReportColumnId}`;
